// Desktop context-rail preference, persisted locally per shell surface.
//
// Versioned localStorage only for now: a server-backed preference would need the
// (currently unused) user_preferences table wired end to end, which is schema and
// API work this stage does not require. The storage payload carries a version so a
// later server-backed migration can read and upgrade it safely. Mobile is never
// affected — below the desktop breakpoint the rail is always overlay-based and
// starts closed regardless of this preference.

export const RAIL_PREFS_STORAGE_KEY = 'ertzyx_shell_rail_prefs_v1';

export type RailPreference = 'always' | 'remember' | 'collapsed';
export type ShellSurface = 'individual' | 'institution';

type SurfacePrefs = {
  mode: RailPreference;
  perRoute: Record<string, boolean>;
};

export type RailPrefsFile = {
  version: 1;
  surfaces: Partial<Record<ShellSurface, SurfacePrefs>>;
};

const DEFAULT_PREFS: RailPrefsFile = { version: 1, surfaces: {} };
const DEFAULT_MODE: RailPreference = 'remember';
// Kept bounded so a long-lived profile cannot grow the payload without limit.
const MAX_REMEMBERED_ROUTES = 40;

function isRailPreference(value: unknown): value is RailPreference {
  return value === 'always' || value === 'remember' || value === 'collapsed';
}

export function readRailPrefs(): RailPrefsFile {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(RAIL_PREFS_STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      (parsed as { version?: unknown }).version !== 1 ||
      typeof (parsed as { surfaces?: unknown }).surfaces !== 'object'
    ) {
      return DEFAULT_PREFS;
    }
    const surfaces: RailPrefsFile['surfaces'] = {};
    for (const surface of ['individual', 'institution'] as const) {
      const entry = (parsed as RailPrefsFile).surfaces?.[surface];
      if (!entry || typeof entry !== 'object') continue;
      const perRoute: Record<string, boolean> = {};
      if (entry.perRoute && typeof entry.perRoute === 'object') {
        for (const [route, open] of Object.entries(entry.perRoute)) {
          if (typeof open === 'boolean') perRoute[route] = open;
        }
      }
      surfaces[surface] = {
        mode: isRailPreference(entry.mode) ? entry.mode : DEFAULT_MODE,
        perRoute,
      };
    }
    return { version: 1, surfaces };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writeRailPrefs(next: RailPrefsFile): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(RAIL_PREFS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage may be full or unavailable (private mode). The preference then
    // simply does not persist; the in-memory state still works for the session.
  }
}

export function getSurfaceMode(prefs: RailPrefsFile, surface: ShellSurface): RailPreference {
  return prefs.surfaces[surface]?.mode ?? DEFAULT_MODE;
}

/** Whether the rail should be open for this surface + route under the stored preference. */
export function resolveRailOpen(
  prefs: RailPrefsFile,
  surface: ShellSurface,
  routeKey: string
): boolean {
  const entry = prefs.surfaces[surface];
  const mode = entry?.mode ?? DEFAULT_MODE;
  if (mode === 'always') return true;
  if (mode === 'collapsed') return false;
  return entry?.perRoute[routeKey] ?? true;
}

/** Records a manual open/close. Only 'remember' mode persists per-route state. */
export function recordRailToggle(
  prefs: RailPrefsFile,
  surface: ShellSurface,
  routeKey: string,
  open: boolean
): RailPrefsFile {
  const entry = prefs.surfaces[surface] ?? { mode: DEFAULT_MODE, perRoute: {} };
  if (entry.mode !== 'remember') return prefs;
  const routes = Object.entries({ ...entry.perRoute, [routeKey]: open });
  const bounded = Object.fromEntries(routes.slice(-MAX_REMEMBERED_ROUTES));
  return {
    version: 1,
    surfaces: { ...prefs.surfaces, [surface]: { ...entry, perRoute: bounded } },
  };
}

export function setSurfaceMode(
  prefs: RailPrefsFile,
  surface: ShellSurface,
  mode: RailPreference
): RailPrefsFile {
  const entry = prefs.surfaces[surface] ?? { mode: DEFAULT_MODE, perRoute: {} };
  return {
    version: 1,
    surfaces: { ...prefs.surfaces, [surface]: { ...entry, mode } },
  };
}
